import { useMemo } from 'react';
import { Close } from '@mui/icons-material';
import theme from 'theme';
import { CampaignChannels, EmailSendInputState } from 'domains/campaigns/types';
import IconButton from 'domains/core/components/IconButton';
import buildCampaignGraph from 'domains/campaigns/utils/buildCampaignGraph';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import GraphNodeBlock from './GraphNodeBlock';

export type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    onRemoveClick: (index: number) => void;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const nodeWrapperStyles = {
    alignItems: 'center',
    display: 'flex',
};

const GraphCampaignNavigation = ({
    activeSendIndex,
    activeStep,
    campaignState,
    onRemoveClick,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const graph = useMemo(() => buildCampaignGraph(campaignState.sends, undefined, campaignState.abTestConfig), [
        campaignState.sends,
        campaignState.abTestConfig,
    ]);

    const lastSendIndex = campaignState.sends.length - 1;
    const lastSend = campaignState.sends[lastSendIndex];
    const lastSendSplitType =
        lastSend && 'splitType' in lastSend ? (lastSend as EmailSendInputState).splitType : undefined;

    const canRemoveLastSend =
        lastSendIndex > 0 &&
        lastSend?.channel === CampaignChannels.EMAIL &&
        !lastSendSplitType &&
        !campaignState.abTestConfig;

    return (
        <div data-testid="graph-campaign-navigation" role="navigation" aria-label="campaign workflow steps">
            {graph.nodes.map((node) => {
                // Skip rows where GraphNodeBlock would return null to avoid rendering orphan borders/buttons
                const isHiddenEmailSend =
                    node.type === 'send' &&
                    node.data?.channel === CampaignChannels.EMAIL &&
                    !node.data?.templateId &&
                    !node.data?.splitType;
                const isHiddenAudience = node.type === 'audience' && !node.data?.segmentId && !node.data?.cohortId;
                if (isHiddenEmailSend || isHiddenAudience) return null;

                const canRemove = node.type === 'send' && node.data?.sendIndex === lastSendIndex && canRemoveLastSend;
                const blockWrapperStyles = {
                    border: canRemove ? `1px dashed ${theme.palette.primary.main}` : 'none',
                    borderRadius: '8px',
                    marginRight: '8px',
                    width: '90%',
                };

                return (
                    <div key={node.id} style={nodeWrapperStyles}>
                        <div style={blockWrapperStyles}>
                            <GraphNodeBlock
                                node={node}
                                activeSendIndex={activeSendIndex}
                                activeStep={activeStep}
                                setActiveSendIndex={setActiveSendIndex}
                                setActiveStep={setActiveStep}
                                readonly={false}
                            />
                        </div>
                        {canRemove && node.data?.sendIndex != null && (
                            <IconButton
                                data-testid="graph-node-send-remove"
                                handleClick={() => onRemoveClick(node.data.sendIndex)}
                                Icon={Close}
                                title="remove send"
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default GraphCampaignNavigation;
